import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Play, Pause, SkipBack, SkipForward, RotateCcw, RotateCw, X, Volume2, VolumeX } from 'lucide-react';
import { useExperience, isWebsite } from '@/store/experience';
import { STATION_NAME, TRACKS } from '@/data/tracks';

const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '0:00');

// StorySpark Sounds: a floating cloud that opens into a glass radio player.
export function RadioStation() {
  const phase = useExperience((s) => s.phase);
  const audioRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const track = TRACKS[index];

  useEffect(() => {
    const a = new Audio();
    a.preload = 'auto';
    audioRef.current = a;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    // Station runs through the playlist in order and loops back to the first song
    const onEnd = () => setIndex((i) => (i + 1) % TRACKS.length);
    a.addEventListener('timeupdate', onTime); a.addEventListener('loadedmetadata', onMeta); a.addEventListener('ended', onEnd);
    a.addEventListener('play', () => setPlaying(true)); a.addEventListener('pause', () => setPlaying(false));
    return () => { a.pause(); a.src = ''; };
  }, []);

  // Auto-start the station when the visitor lands on the website (they've already interacted at the gate)
  const started = useRef(false);
  useEffect(() => {
    if (!isWebsite(phase) || started.current) return;
    started.current = true;
    const a = audioRef.current;
    const tryPlay = () => a.play().catch(() => { window.addEventListener('pointerdown', () => a.play().catch(() => {}), { once: true }); });
    if (!a.src) { a.src = track.src; a.load(); }
    tryPlay();
  }, [phase, track.src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.src && a.src.endsWith(track.src)) return;
    const wasPlaying = !a.paused || a.ended;
    a.src = track.src; a.load(); setTime(0);
    if (wasPlaying) a.play().catch(() => {});
  }, [track.src]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => { const a = audioRef.current; if (a.paused) a.play().catch(() => {}); else a.pause(); };
  const seekBy = (d) => { const a = audioRef.current; a.currentTime = Math.min(Math.max(0, a.currentTime + d), a.duration || Infinity); };
  const seekTo = (e) => { const r = e.currentTarget.getBoundingClientRect(); const p = (e.clientX - r.left) / r.width; if (duration) audioRef.current.currentTime = p * duration; };
  const prev = () => { const a = audioRef.current; if (a.currentTime > 3 || TRACKS.length === 1) a.currentTime = 0; else setIndex((i) => (i - 1 + TRACKS.length) % TRACKS.length); };
  const next = () => { if (TRACKS.length === 1) { audioRef.current.currentTime = 0; return; } setIndex((i) => (i + 1) % TRACKS.length); };
  const toggleMute = () => { audioRef.current.muted = !muted; setMuted(!muted); };

  if (!isWebsite(phase)) return null;
  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <>
      <motion.button type="button" onClick={() => setOpen((o) => !o)} className="fixed left-4 bottom-4 sm:left-8 sm:bottom-8 z-40 glass cloud-shape flex items-center gap-3 pl-4 pr-5 py-3 text-left"
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: [0, -6, 0] }} transition={{ opacity: { duration: 1 }, y: { duration: 6, repeat: Infinity, ease: 'easeInOut' } }}
        whileHover={{ scale: 1.05 }} style={{ boxShadow: 'inset 0 0 30px 6px rgba(255,255,255,0.25), 0 20px 50px rgba(3,12,36,0.4)' }} data-testid="radio-cloud-button" data-cursor="cloud" aria-label="Open StorySpark Sounds">
        <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
          <Radio size={16} className={playing ? 'text-[#ffb37a]' : 'text-white'} />
          {playing && <span className="absolute inset-0 rounded-full border border-[#f97316]/60 animate-ping" />}
        </span>
        <span>
          <span className="block font-display text-[0.6rem] tracking-[0.3em] uppercase text-white/65">Radio</span>
          <span className="block font-display text-sm font-semibold leading-tight">{STATION_NAME}</span>
        </span>
        {playing && (
          <span className="flex items-end gap-[3px] h-4 ml-1" aria-hidden>
            {[0, 1, 2, 3].map((i) => <motion.span key={i} className="w-[3px] rounded-full bg-[#f97316]" animate={{ height: ['30%', '100%', '45%'] }} transition={{ duration: 0.7 + i * 0.13, repeat: Infinity, ease: 'easeInOut' }} />)}
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div key="radio-panel" className="fixed left-4 right-4 bottom-24 sm:left-8 sm:right-auto sm:bottom-28 z-40 w-auto sm:w-[380px] glass-deep rounded-[2rem] p-6 overflow-hidden"
            initial={{ opacity: 0, y: 30, scale: 0.9, filter: 'blur(10px)' }} animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }} exit={{ opacity: 0, y: 20, scale: 0.92, filter: 'blur(10px)', transition: { duration: 0.25 } }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ boxShadow: 'inset 0 0 50px 10px rgba(255,255,255,0.18), 0 30px 80px rgba(2,10,30,0.55)' }} role="dialog" aria-label="StorySpark Sounds player" data-testid="radio-panel">
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.3), rgba(249,115,22,0) 70%)', filter: 'blur(14px)' }} />
            <div className="flex items-start justify-between">
              <div>
                <p className="cloud-eyebrow flex items-center gap-2"><Radio size={11} /> {STATION_NAME}</p>
                <p className="mt-3 font-display text-xl font-bold leading-tight" data-testid="radio-track-title">{track.title}</p>
                <p className="text-sm text-white/65" data-testid="radio-track-artist">{track.artist}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 text-white/60 hover:text-white hover:bg-white/10 transition-colors" aria-label="Close player" data-testid="radio-close-button"><X size={16} /></button>
            </div>

            <div className="mt-6 group cursor-pointer" onClick={seekTo} role="slider" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} data-testid="radio-progress">
              <div className="h-1.5 w-full rounded-full bg-white/15 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #f97316, #ffb37a)', boxShadow: '0 0 12px rgba(249,115,22,0.7)' }} />
              </div>
              <div className="mt-2 flex justify-between font-display text-[0.62rem] tracking-[0.2em] text-white/55">
                <span data-testid="radio-time">{fmt(time)}</span><span data-testid="radio-duration">{fmt(duration)}</span>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <button type="button" onClick={toggleMute} className="rounded-full p-2 text-white/70 hover:text-white transition-colors" aria-label="Mute" data-testid="radio-mute-button">{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
              <div className="flex items-center gap-1 sm:gap-2">
                <button type="button" onClick={prev} className="rounded-full p-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors" aria-label="Previous track" data-testid="radio-prev-button"><SkipBack size={18} /></button>
                <button type="button" onClick={() => seekBy(-10)} className="rounded-full p-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors" aria-label="Rewind 10 seconds" data-testid="radio-rewind-button"><RotateCcw size={18} /></button>
                <button type="button" onClick={toggle} className="rounded-full p-4 bg-[#f97316] text-white shadow-[0_0_30px_rgba(249,115,22,0.6)] hover:bg-[#fb8a3c] transition-colors" aria-label={playing ? 'Pause' : 'Play'} data-testid="radio-play-button">{playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}</button>
                <button type="button" onClick={() => seekBy(10)} className="rounded-full p-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors" aria-label="Fast forward 10 seconds" data-testid="radio-forward-button"><RotateCw size={18} /></button>
                <button type="button" onClick={next} className="rounded-full p-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors" aria-label="Next track" data-testid="radio-next-button"><SkipForward size={18} /></button>
              </div>
              <span className="font-display text-[0.6rem] tracking-[0.2em] text-white/50" data-testid="radio-track-count">{index + 1}/{TRACKS.length}</span>
            </div>

            <ul className="mt-5 border-t border-white/10 pt-4 space-y-1 max-h-32 overflow-y-auto no-scrollbar" data-testid="radio-playlist">
              {TRACKS.map((t, i) => (
                <li key={t.id}>
                  <button type="button" onClick={() => { setIndex(i); setTimeout(() => audioRef.current?.play().catch(() => {}), 50); }} className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors ${i === index ? 'bg-white/12 text-white' : 'text-white/65 hover:bg-white/8 hover:text-white'}`} data-testid={`radio-track-${t.id}`}>
                    <span><span className="font-medium">{t.title}</span> <span className="text-white/50">· {t.artist}</span></span>
                    {i === index && playing && <span className="h-1.5 w-1.5 rounded-full bg-[#f97316] shadow-[0_0_10px_#f97316]" />}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
